import {
  createContext,
  useState,
  ReactNode,
  useContext,
} from "react";
import {
  Contrato,
  Parcela,
  contratos as dummy,
} from "../services/data/dummyContratos";
import { useMutation } from "@tanstack/react-query";
import { apiModulo } from "../services/api";
import { ConsultaContratosData } from '../types/contratos-data-interface'
import { ConsultaParametrosData } from '../types/simular-acordo-interface'
import { RequestDivida, Divida } from '../types/divida-interface'

interface ContratoContextProviderProps {
  children: ReactNode;
}

interface RequestConsultaContratos {
  cpf_cnpj: string;
  credor: number;
  url: string;
  app?: string;
  isOperador?: boolean
  usuario?: string
}

interface RequestConsultaParamentros {
  cpf_cnpj: string;
  codcredor: string;
  formapagamento: string;
  contrato: string;
  url: string;
  app?: string
  usuario?: string
}


interface ContratoContextProps {
  showParcelas: string[];
  parcelasParaNegociar: Parcela[];
  contratos: Contrato[];
  showParcelasContrato: (contrato: string) => void;
  isJuncao?: boolean;
  contratoParaNegociar: (
    parcelas: Parcela[],
    juncao?: boolean,
    contrato?: string
  ) => void;
  parcelaParaNegociar: (parcela: Parcela) => void;
  negociarAcordo: () => void;
  tabularContrato: () => void;
  retirarTabulacaoContrato: (contrato: string) => void;
  limparNegociacoes: () => void;
  limparNegociacoesContratos: () => void;
  getContracts({ cpf_cnpj, credor, url }: RequestConsultaContratos): Promise<ConsultaContratosData>
  contratosData: ConsultaContratosData
  getConsultaParamentros({ cpf_cnpj, codcredor, url, formapagamento, contrato }: RequestConsultaParamentros): Promise<void>
  consultParamentros: ConsultaParametrosData
  clearContratos(): void
  getDividasWS({ cpf_cnpj, codcredor, contrato, url }: RequestDivida): Promise<Divida[]>
  getContratoCredor(contrato: ConsultaContratosData): void
  todosContratosData: ConsultaContratosData
  dividaData: Divida[]
}

const ContratoContext = createContext<ContratoContextProps>(
  {} as ContratoContextProps
);

export const ContratoProvider = ({
  children,
}: ContratoContextProviderProps) => {
  const [showParcelas, setShowParcelas] = useState<string[]>([]);
  const [parcelasParaNegociar, setParcelasParaNegociar] = useState<Parcela[]>(
    []
  );
  const [contratos, setContratos] = useState<Contrato[]>(dummy);
  const [isJuncao, setIsJuncao] = useState<boolean>();
  const [contratosData, setContratosData] = useState<ConsultaContratosData>({} as ConsultaContratosData)
  const [todosContratosData, setTodosContratosData] = useState<ConsultaContratosData>({} as ConsultaContratosData)
  const [consultParamentros, setConsultaParamentos] = useState<ConsultaParametrosData>({} as ConsultaParametrosData)
  const [dividaData, setDividaData] = useState<Divida[]>([])


  const consultaMutation = useMutation({
    mutationFn: ({ cpf_cnpj, credor, url, app, usuario }: RequestConsultaContratos) => apiModulo.post('/contracts', {
      url,
    }, { params: { cpf_cnpj, codcredor: credor, app: app || 'ACIONA_WEB', usuario } }), mutationKey: ['form']
  })

  //Mudar mutationKey, para só pegar as mutations que estão nas telas de atendimento
  const consultaParametros = useMutation({
    mutationFn: ({ cpf_cnpj, codcredor, contrato, formapagamento, url, app, usuario }: RequestConsultaParamentros) => apiModulo.post('/simulate/params', {
      url,
      cpf_cnpj,
      codcredor,
      formapagamento,
      contrato, app: app || 'ACIONA_WEB',
      usuario
    },), mutationKey: ['form']
  })

  const dividaWS = useMutation({
    mutationFn: ({ cpf_cnpj, codcredor, contrato, url, app, usuario }: RequestDivida) => apiModulo.post('/debts/ws', {
      url,
      cpf_cnpj, codcredor, contrato, app: app || 'ACIONA_WEB', usuario
    }), mutationKey: ['form']
  })

  function clearContratos() {
    setContratosData({} as ConsultaContratosData)
    setConsultaParamentos({} as ConsultaParametrosData)
    setDividaData([])
  }

  async function getDividasWS({ cpf_cnpj, codcredor, contrato, url, app }: RequestDivida) {
    const { data } = await dividaWS.mutateAsync({ cpf_cnpj, codcredor, contrato, url, app })
    setDividaData(data.message)
    return data.message
  }

  async function getContracts({ cpf_cnpj, credor, url, app, isOperador, usuario }: RequestConsultaContratos) {
    setContratosData({} as ConsultaContratosData)
    const { data } = await consultaMutation.mutateAsync({ cpf_cnpj, credor, url, app, usuario })
    if (isOperador) {
      setTodosContratosData(data.message)
    }
    setConsultaParamentos({} as ConsultaParametrosData)
    setContratosData(data.message)
    return data.message
  }

  function getContratoCredor(contrato: ConsultaContratosData) {
    setContratosData(contrato)
  }

  async function getConsultaParamentros({ cpf_cnpj, codcredor, url, formapagamento, contrato, usuario }: RequestConsultaParamentros) {
    const { data } = await consultaParametros.mutateAsync({ cpf_cnpj, codcredor: String(codcredor), url, contrato, formapagamento, usuario })
    setConsultaParamentos(data.message)
  }

  function contratoParaNegociar(
    parcelas: Parcela[],
    juncao?: boolean,
    contrato?: string
  ) {
    const find = parcelasParaNegociar.filter(
      (item) =>
        item.contrato === contrato &&
        item.nrParcela ===
        parcelas.find((parc) => parc.nrParcela === item.nrParcela)?.nrParcela
    );

    if (find && find.length > 0) {
      const parcelasFiltradas = parcelasParaNegociar.filter((item) => {
        if (contrato) {
          return item.contrato !== contrato;
        } else {
          const parcEncontrada = parcelas.find(
            (parc) =>
              parc.nrParcela === item.nrParcela && item.contrato === contrato
          );
          return !parcEncontrada;
        }
      });
      setParcelasParaNegociar(parcelasFiltradas);

      // Remover contrato de showParcelas se for um contrato inteiro
      if (contrato) {
        const contratosFiltrados = showParcelas.filter((c) => c !== contrato);
        setShowParcelas(contratosFiltrados);
        setIsJuncao(contratosFiltrados.length <= 0 ? undefined : juncao);
        // console.log(contratosFiltrados.length, juncao);
      }
    } else {
      // Adicionar parcelas no parcelasParaNegociar
      const showFind = showParcelas.find((item) => item === contrato);
      if (contrato && !showFind) {
        setShowParcelas([...showParcelas, contrato]);
      }
      setParcelasParaNegociar([...parcelasParaNegociar, ...parcelas]);
      // if (juncao !== isJuncao) {
      setIsJuncao(juncao);
      // }
    }
  }

  function showParcelasContrato(contrato: string) {
    const find = showParcelas.find((item) => item === contrato);
    if (find && find.length > 0) {
      const contratosFiltrados = showParcelas.filter((c) => c !== contrato);
      setShowParcelas(contratosFiltrados);

    } else {

      setShowParcelas([...showParcelas, contrato]);
    }
  }

  function parcelaParaNegociar(parcela: Parcela) {
    const parcEncontrada = parcelasParaNegociar.find(
      (parc) =>
        parc.nrParcela === parcela.nrParcela &&
        parc.contrato === parcela.contrato
    );
    if (parcEncontrada) {
      const parcelasFiltradas = parcelasParaNegociar.filter(
        (parc) =>
          parc.nrParcela !== parcela.nrParcela ||
          parc.contrato !== parcela.contrato
      );
      setParcelasParaNegociar(parcelasFiltradas);
    } else {
      setParcelasParaNegociar([...parcelasParaNegociar, parcela]);
    }
  }

  function negociarAcordo() {
    let contratosNegociaveis = parcelasParaNegociar.map((parc) => {
      return parc.contrato;
    });
    let contratosUnicos = contratosNegociaveis.filter((contrato, index) => {
      return contratosNegociaveis.indexOf(contrato) === index;
    });

    setContratos(
      contratos.map((c) => {
        if (contratosUnicos.find((item) => item === c.contrato)) {
          return {
            ...c,
            negociado: true,
            tabulado: true,
          };
        } else {
          return c;
        }
      })
    );
  }

  function tabularContrato() {
    let contratosNegociaveis = parcelasParaNegociar.map((parc) => {
      return parc.contrato;
    });
    let contratosUnicos = contratosNegociaveis.filter((contrato, index) => {
      return contratosNegociaveis.indexOf(contrato) === index;
    });

    setContratos(
      contratos.map((c) => {
        if (contratosUnicos.find((item) => item === c.contrato)) {
          return {
            ...c,
            tabulado: true,
          };
        } else {
          return c;
        }
      })
    );

    setParcelasParaNegociar([]);
    setShowParcelas([]);
    setIsJuncao(undefined);
    const element = document.getElementById("neg-title");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }

  function retirarTabulacaoContrato(contrato: string) {
    setContratos(
      contratos.map((c) => {
        if (contrato === c.contrato) {
          return {
            ...c,
            tabulado: false,
          };
        } else {
          return c;
        }
      })
    );
    const element = document.getElementById("neg-title");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }

  function limparNegociacoes() {
    setParcelasParaNegociar([]);
    setShowParcelas([]);
    setIsJuncao(undefined);
  }

  function limparNegociacoesContratos() {
    setParcelasParaNegociar([]);
    setShowParcelas([]);
    setIsJuncao(undefined);
    setContratos(dummy);
  }

  return (
    <ContratoContext.Provider
      value={{
        showParcelas,
        showParcelasContrato,
        isJuncao,
        parcelaParaNegociar,
        negociarAcordo,
        contratoParaNegociar,
        parcelasParaNegociar,
        tabularContrato,
        retirarTabulacaoContrato,
        contratos,
        limparNegociacoes,
        limparNegociacoesContratos,
        contratosData,
        getContracts,
        consultParamentros,
        getConsultaParamentros,
        clearContratos,
        getDividasWS,
        dividaData,
        getContratoCredor,
        todosContratosData
      }}
    >
      {children}
    </ContratoContext.Provider>
  );
};

export const useContrato = () => useContext(ContratoContext);
